/**
 * AG-UI event handlers for the chat store.
 *
 * Preserves 1:1 behavioral parity with the original useChat.ts handleEvent
 * callback, but operates via Immer mutations on Zustand state instead of
 * React setMessages/setState.
 *
 * CRITICAL CONTRACT — do not change semantics:
 *   - TEXT_MESSAGE_CONTENT auto-exits reasoning mode
 *   - TOOL_CALL_ARGS: JSON.parse entire delta (not incremental)
 *   - TOOL_CALL_RESULT: "Error: " prefix detection from backend
 *   - STATE_DELTA: top-level paths only, reject nested
 */

import type { AGUIEvent, AppState } from "@strands/agent-transport";
import type { Part, TextPart, ReasoningPart, ToolPart, ToolState } from "../types";
import type { SessionState } from "./session-state";
import { getOrCreateParts } from "./session-state";

// ---------------------------------------------------------------------------
// Types for the Immer-compatible set/get from Zustand
// ---------------------------------------------------------------------------

type GetState = () => ChatStoreState;
type SetState = (fn: (draft: ChatStoreState) => void) => void;

/** Minimal interface — the full ChatStore type lives in chat-store.ts */
interface ChatStoreState extends SessionState {
  activeSessionId: string | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function lastAssistantId(state: ChatStoreState): string | null {
  for (let i = state.messages.length - 1; i >= 0; i--) {
    if (state.messages[i]!.role === "assistant") return state.messages[i]!.id;
  }
  return null;
}

function appendTextPart(
  state: ChatStoreState,
  type: "text" | "reasoning",
  content: string,
) {
  const msgId = lastAssistantId(state);
  if (!msgId) return;
  const parts = getOrCreateParts(state.partsMap, msgId);
  const last = parts[parts.length - 1];

  if (type === "text" && last?.type === "text") {
    (last as TextPart).text += content;
  } else if (type === "reasoning" && last?.type === "reasoning") {
    (last as ReasoningPart).text += content;
  } else {
    const part: Part =
      type === "text" ? { type: "text", text: content } : { type: "reasoning", text: content };
    parts.push(part);
  }
}

function upsertToolPart(
  state: ChatStoreState,
  toolCallId: string,
  patch: { tool?: string; state?: Partial<ToolState>; id?: string },
) {
  const msgId = lastAssistantId(state);
  if (!msgId) return;
  const parts = getOrCreateParts(state.partsMap, msgId);
  const existing = parts.find(
    (p): p is ToolPart => p.type === "tool" && p.id === toolCallId,
  );

  if (existing) {
    if (patch.tool !== undefined) existing.tool = patch.tool;
    if (patch.state) {
      Object.assign(existing.state, patch.state);
    }
  } else {
    parts.push({
      type: "tool",
      id: toolCallId,
      tool: patch.tool ?? "",
      state: {
        status: "running",
        input: {},
        ...patch.state,
      },
    });
  }
}

// ---------------------------------------------------------------------------
// Event router
// ---------------------------------------------------------------------------

export function routeEvent(
  event: AGUIEvent,
  set: SetState,
  get: GetState,
  expectedSessionId: string | null,
): void {
  // Single point of enforcement: drop the event if the user has navigated
  // away from the session it belongs to. Callers (useAgentStream subscriber,
  // useSessionAttach reader) already guard their own loops, but a stale
  // event from a tab that just switched sessions could still slip past
  // their checks if React's setState batching delays the activeSessionId
  // update. Comparing against the caller-supplied `expectedSessionId`
  // keeps the agent A → store B leak impossible regardless.
  const cur = get().activeSessionId;
  if (expectedSessionId === null || cur !== expectedSessionId) return;

  switch (event.type) {
    // ----- Text -----
    case "TEXT_MESSAGE_START":
      break;

    case "TEXT_MESSAGE_CONTENT":
      set((s) => {
        // Auto-exit reasoning mode (useChat.ts line 142)
        if (s.inReasoning) s.inReasoning = false;
        appendTextPart(s, "text", event.delta as string);
      });
      break;

    case "TEXT_MESSAGE_END":
      break;

    // ----- Reasoning -----
    case "REASONING_START":
      set((s) => {
        s.inReasoning = true;
      });
      break;

    case "REASONING_MESSAGE_CONTENT":
      set((s) => {
        appendTextPart(s, "reasoning", event.delta as string);
      });
      break;

    case "REASONING_END":
      set((s) => {
        s.inReasoning = false;
      });
      break;

    // ----- Tool calls -----
    case "TOOL_CALL_START": {
      const id = event.toolCallId as string;
      set((s) => {
        upsertToolPart(s, id, {
          tool: event.toolCallName as string,
          state: { status: "running", input: {} },
        });
      });
      break;
    }

    case "TOOL_CALL_ARGS": {
      const id = event.toolCallId as string;
      set((s) => {
        // Parse entire delta as JSON (not incremental like midgard)
        try {
          const args = JSON.parse(event.delta as string) as Record<string, unknown>;
          upsertToolPart(s, id, { state: { status: "running", input: args } });
        } catch {
          /* skip malformed */
        }
      });
      break;
    }

    case "TOOL_CALL_END":
      break;

    case "TOOL_CALL_RESULT": {
      // Backend encodes tool failures as "Error: ..." prefix in content
      const id = event.toolCallId as string;
      const content = (event.content as string) || "";
      const isError = content.startsWith("Error: ");
      set((s) => {
        upsertToolPart(s, id, {
          state: {
            status: isError ? "error" : "completed",
            output: isError ? "" : content,
            error: isError ? content.slice("Error: ".length) : null,
          },
        });
      });
      break;
    }

    // ----- State sync -----
    case "STATE_SNAPSHOT":
      set((s) => {
        s.appState = (event.snapshot as AppState) ?? {};
      });
      break;

    case "STATE_DELTA": {
      // Top-level JSON Patch only — reject nested paths (useChat.ts lines 190-207)
      const ops = event.delta as Array<{ op: string; path: string; value?: unknown }>;
      if (!Array.isArray(ops) || ops.length === 0) break;
      set((s) => {
        for (const op of ops) {
          const key = op.path.startsWith("/") ? op.path.slice(1) : op.path;
          if (!key || key.includes("/")) continue;
          if (op.op === "replace" || op.op === "add") {
            s.appState[key] = op.value;
          } else if (op.op === "remove") {
            delete s.appState[key];
          }
        }
      });
      break;
    }

    // ----- Custom -----
    case "CUSTOM": {
      const evt = event as unknown as { name?: string; value?: unknown };
      // session_title_updated is consumed in the hook layer (it mutates a
      // React Query cache the store can't see). `trace_attached` carries
      // the Langfuse trace_id of the in-flight run — tag the latest
      // assistant message so 👍/👎 in TurnView can post against it.
      if (evt.name === "trace_attached") {
        const { trace_id } = (evt.value ?? {}) as { trace_id?: string };
        if (typeof trace_id === "string" && trace_id) {
          set((s) => {
            for (let i = s.messages.length - 1; i >= 0; i--) {
              const m = s.messages[i]!;
              if (m.role === "assistant") {
                m.traceId = trace_id;
                break;
              }
            }
          });
        }
      }
      break;
    }

    // ----- Errors -----
    case "RUN_ERROR": {
      // AG-UI client synthesizes a RUN_ERROR when the fetch is aborted
      // (e.g. user clicked Stop). That's not an error to show in chat.
      const msg = String(event.message ?? "");
      if (/abort/i.test(msg)) break;
      set((s) => {
        appendTextPart(s, "text", `Ошибка: ${msg}`);
      });
      break;
    }
  }
}
